<template>
  <div class="input-wrapper">
    <div class="flex w-full">
      <div class="relative w-full" :class="!hasFloatLabel && 'flex flex-col-reverse gap-2'">
        <Input
          v-model="modelValue"
          v-bind="$attrs"
          :name="name"
          :type="type"
          :error="error"
          placeholder=""
          :readonly="readonly"
          :class="hasAppend && 'rounded-e-none'"
        />
        <Label
          :for="name"
          :class="[
            'pointer-events-none px-2 text-sm',
            error && '!text-destructive',
            hasFloatLabel &&
              'absolute start-1 top-1 z-10 origin-[0] -translate-y-6 scale-75 transform text-white/60 duration-150 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-1 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:px-2 peer-focus:!text-white/100 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4',
          ]"
          >{{ label || placeholder
          }}{{ (readonly && ` (${t('input.label.readonly')})`) || '' }}</Label
        >
      </div>
      <div
        v-if="$slots.append"
        class="flex h-10 items-center rounded-e-lg border-y border-r border-input/20 bg-background/40 text-sm font-normal shadow-sm transition-colors hover:bg-background/100 hover:text-white/60 focus:border-input/100 focus:outline-none focus:ring-0 focus:ring-transparent disabled:cursor-not-allowed disabled:opacity-50"
        :class="error && 'border-destructive'"
      >
        <slot name="append" />
      </div>
    </div>
    <span class="input-error mt-1" v-if="error">{{ error }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, type VNode } from 'vue';
import Input from '@ui/input/Input.vue';
import Label from '@ui/label/Label.vue';
import { useI18n } from 'vue-i18n';

interface InputProps {
  label: string;
  placeholder: string;
  name: string;
  type?: string;
  error?: string;
  hasFloatLabel?: boolean;
  readonly?: boolean;
}

withDefaults(defineProps<InputProps>(), {
  type: 'text',
  error: undefined,
  hasFloatLabel: true,
  readonly: false,
});

const modelValue = defineModel<string | undefined>('modelValue', {
  required: true,
});

const slots = defineSlots<{
  append?: () => VNode;
}>();

const { t } = useI18n();

const hasAppend = computed(() => !!slots.append);
</script>
